/**
 * DealHealthCard — the deal page's ✦ Deal health card (deal detail slice 6,
 * §39.9 order 6; spec §3.4 A): the score ring, the caption, the headline, the
 * summary sentence, the status pill, the 3×2 grid of checks and the footer.
 * The score, the status and every check's pass are the SERVER's (§40.12, rule
 * set 2 — deal.assessment); dealHealthCard.ts words them; this file draws.
 *
 * Revenue Recovery R3 (§40.13): a failing tile whose check is a flag RULE
 * (close date, contact, recent activity) gains one line and, for an owner or
 * admin, two controls. The line is the latest act on the tile's OPEN episode
 * ("Dismissed by Pat · Customer-side pause · 19 Oct"); the controls confirm or
 * dismiss it. A dismissal changes no fact: the tile keeps its warning ground
 * and its "!" — the check still fails — and the score ring is untouched. A
 * failing tile with NO open episode yet (the ledger is written nightly) says
 * so instead of offering controls (ruling 4). Stage age has no tile — the
 * stage bar renders it — so its episode gets one row under the grid. Nothing
 * here renders for a session that cannot act, beyond the act line itself.
 *
 * Colours are the status tokens (success / warning / danger) — the spec's raw
 * green / orange / red values and its two tile grounds are not added beside
 * them (tokens.ts is the single source; slice 4's deviation 3). The
 * footer is the MUTED ink, not the spec's "faint": text left the faint ink
 * platform-wide in row 206 (rows 204, 206).
 */
import { useState } from 'react'
import { Check } from 'lucide-react'
import { accent, danger, ink, success, surface, warning } from '../../../../theme/tokens'
import type { DealRiskFlag, RiskFlagActionBody } from '../../../../api/types'
import { useWriteAccess } from '../../../../lib/writeAccess'
import { AFTER_TONIGHT_COPY, CHECK_RULE, COMMITTEE_RULE, HEALTH_FOOTER, NO_DECISION_MAKER_FACT, NO_DECISION_MAKER_NOTE, STAGE_AGE_RULE, STATUS_LABEL, headlineFor, type HealthCheck, type HealthStatus } from './dealHealthCard'
import { actionLine } from './dealHealth'
import { actorName } from './dealTimeline'
import { RiskFlagDismissDialog } from './RiskFlagDismissDialog'

const TONE: Record<HealthStatus, { DEFAULT: string; bg: string; text: string }> = {
  'on-track': success,
  'needs-attention': warning,
  'at-risk': danger,
}

const RING = 64, STROKE = 7, R = (RING - STROKE) / 2, CIRC = 2 * Math.PI * R

export interface RiskActProps {
  /** The deal's open episodes with their latest act (deal.riskFlags). */
  riskFlags?: readonly DealRiskFlag[] | null
  /** The session's /v1/me lists the tenant's sales.risk_flags switch as on. */
  riskFlagsOn?: boolean
  /** The server says this session may act on the deal's flags (R4:
   *  viewerMayAct — owner/admin, or the deal's linked owner). Fail closed. */
  canDecide?: boolean
  /** The rule whose act is in flight (controls disabled), or null. */
  acting?: string | null
  onAct?: (rule: string, body: RiskFlagActionBody) => void
}

/** One tile's (or the stage-age row's) act line and controls. */
function FlagActions({ rule, label, episode, props }: { rule: string; label: string; episode: DealRiskFlag | null; props: RiskActProps }) {
  const [dismissing, setDismissing] = useState(false)
  const { riskFlagsOn = false, canDecide = false, acting = null, onAct } = props
  const line = episode?.action ? actionLine(episode.action, actorName) : null
  const latest = episode?.action?.action ?? null
  const busy = acting === rule
  const canAct = canDecide && riskFlagsOn && !!onAct
  // Nothing to show (no act line, no session that can act): render NOTHING,
  // not an empty wrapper — a stray margin moved two visual frames by 6 px
  // for a null session (found by the first R3 shoot).
  if (!line && !canAct) return null
  return (
    <div className="mt-1.5 flex flex-col gap-1" data-flag-rule={rule} data-flag-state={episode ? (latest ?? 'open') : 'none'}>
      {line && (
        <div className="text-12 text-ink-muted" data-flag-action={latest ?? undefined}>
          {line}{episode?.action?.note ? <span> · “{episode.action.note}”</span> : null}
        </div>
      )}
      {canAct && episode && (
        <div className="flex items-center gap-1.5">
          {latest !== 'confirm' && (
            <button type="button" className="k-btn-secondary text-12 py-0.5 px-2" disabled={busy}
              aria-label={`Confirm ${label} flag`} onClick={() => onAct(rule, { action: 'confirm' })}>
              {busy ? 'Saving…' : 'Confirm'}
            </button>
          )}
          {latest !== 'dismiss' && (
            <button type="button" className="k-btn-secondary text-12 py-0.5 px-2" disabled={busy}
              aria-label={`Dismiss ${label} flag`} onClick={() => setDismissing(true)}>
              Dismiss
            </button>
          )}
        </div>
      )}
      {canAct && !episode && (
        <div className="text-12 text-ink-muted" data-flag-pending>{AFTER_TONIGHT_COPY}</div>
      )}
      {dismissing && (
        <RiskFlagDismissDialog ruleLabel={label} submitting={busy} onCancel={() => setDismissing(false)}
          onSubmit={(reason, note) => { setDismissing(false); onAct?.(rule, note ? { action: 'dismiss', reason, note } : { action: 'dismiss', reason }) }} />
      )}
    </div>
  )
}

export function DealHealthCard({ checks, summary, score, status, stageOver = false, stageDays = null, noDecisionMaker = false, ...act }: {
  checks: readonly HealthCheck[]
  summary: string
  /** The server's 0–6 score and its status (assessment.score / .status). */
  score: number
  status: HealthStatus
  /** The server's stage age over its threshold (assessment.stage.over) and
   *  its day count — the stage-age row under the grid (R3). */
  stageOver?: boolean
  stageDays?: number | null
  /** R6: the server's no_decision_maker signal is on this deal (rule set 3) —
   *  the committee row under the grid. Never scored. */
  noDecisionMaker?: boolean
} & RiskActProps) {
  // Who may set a committee role: owner/admin, or (act.canDecide) the deal's
  // linked owner — the People card's own rule. Anyone else reads the fact only.
  const { records } = useWriteAccess()
  const tone = TONE[status]
  const episodes = act.riskFlags ?? null
  const episodeFor = (rule: string): DealRiskFlag | null => episodes?.find(f => f.rule === rule) ?? null
  const showActs = act.riskFlagsOn === true || (episodes !== null && episodes.length > 0)
  const stageEpisode = episodeFor(STAGE_AGE_RULE)
  // The row exists to carry an act: it renders only when there is a line to
  // show or a session that can act — never as a second copy of the stage bar.
  const canAct = act.canDecide === true && act.riskFlagsOn === true && !!act.onAct
  const showStageRow = showActs && (stageOver || stageEpisode !== null) && (stageEpisode?.action != null || canAct)
  const committeeEpisode = episodeFor(COMMITTEE_RULE)
  // The committee row shows the server's LIVE signal as a fact (whoever reads
  // the page) and carries its act. Once a decision maker is set the signal is
  // gone and so is the row — its sentence would no longer be true, even though
  // the ledger episode stays open until tonight's run closes it.
  const showCommitteeRow = noDecisionMaker
  return (
    <section className="k-card p-5" data-deal-health data-score={score} data-status={status} aria-label="Deal health">
      <div className="flex items-start gap-4">
        <div className="relative flex-shrink-0" style={{ width: RING, height: RING }}>
          <svg width={RING} height={RING} viewBox={`0 0 ${RING} ${RING}`} aria-hidden="true">
            <circle cx={RING / 2} cy={RING / 2} r={R} fill="none" strokeWidth={STROKE} style={{ stroke: surface.control }} />
            <circle cx={RING / 2} cy={RING / 2} r={R} fill="none" strokeWidth={STROKE} strokeLinecap="round"
              strokeDasharray={`${(CIRC * score) / 6} ${CIRC}`} transform={`rotate(-90 ${RING / 2} ${RING / 2})`}
              style={{ stroke: tone.DEFAULT }} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center text-14 font-semibold text-ink-primary" data-health-score>
            {score}/6
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-12 font-semibold" style={{ color: accent.DEFAULT }}>✦ Deal health</div>
          <div className="text-[19px] font-semibold text-ink-primary leading-snug mt-0.5" data-health-headline>{headlineFor(score)}</div>
          {summary && <p className="text-13 text-ink-muted mt-1" data-health-summary>{summary}</p>}
        </div>
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-12 font-medium flex-shrink-0"
          style={{ background: tone.bg, color: tone.text }} data-health-pill>
          {STATUS_LABEL[status]}
        </span>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4" aria-label="Health checks">
        {checks.map(c => {
          const rule = CHECK_RULE[c.id]
          return (
            <li key={c.id} className="rounded-[12px] p-3 flex items-start gap-2.5" data-check={c.id} data-pass={c.pass ? 'true' : 'false'}
              style={{ background: c.pass ? success.bg : warning.bg }}>
              <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-12 font-bold"
                style={{ background: c.pass ? success.DEFAULT : warning.DEFAULT, color: ink['on-accent'] }} aria-hidden="true">
                {c.pass ? <Check size={12} strokeWidth={3} /> : '!'}
              </span>
              <div className="min-w-0">
                <div className="text-13 font-semibold text-ink-primary">
                  {c.label}<span className="sr-only">{c.pass ? ': passed' : ': needs attention'}</span>
                </div>
                <div className="text-12 text-ink-muted">{c.note}</div>
                {!c.pass && rule && showActs && <FlagActions rule={rule} label={c.label} episode={episodeFor(rule)} props={act} />}
              </div>
            </li>
          )
        })}
      </ul>
      {showStageRow && (
        <div className="rounded-[12px] p-3 mt-3 flex items-start gap-2.5" data-stage-age-row style={{ background: warning.bg }}>
          <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-12 font-bold"
            style={{ background: warning.DEFAULT, color: ink['on-accent'] }} aria-hidden="true">!</span>
          <div className="min-w-0 flex-1">
            <div className="text-13 font-semibold text-ink-primary">Stage age<span className="sr-only">: needs attention</span></div>
            <div className="text-12 text-ink-muted">{stageDays != null ? `${stageDays}d in stage, over the threshold` : 'Over the stage threshold'}</div>
            <FlagActions rule={STAGE_AGE_RULE} label="Stage age" episode={stageEpisode} props={act} />
          </div>
        </div>
      )}
      {showCommitteeRow && (
        <div className="rounded-[12px] p-3 mt-3 flex items-start gap-2.5" data-committee-row style={{ background: warning.bg }}>
          <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-12 font-bold"
            style={{ background: warning.DEFAULT, color: ink['on-accent'] }} aria-hidden="true">!</span>
          <div className="min-w-0 flex-1">
            <div className="text-13 font-semibold text-ink-primary">Decision maker<span className="sr-only">: needs attention</span></div>
            <div className="text-12 text-ink-muted">{records || act.canDecide === true ? NO_DECISION_MAKER_NOTE : NO_DECISION_MAKER_FACT}</div>
            {showActs && <FlagActions rule={COMMITTEE_RULE} label="Decision maker" episode={committeeEpisode} props={act} />}
          </div>
        </div>
      )}
      <p className="text-12 text-ink-muted mt-4">{HEALTH_FOOTER}</p>
    </section>
  )
}
